import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api } from "../../shared/api/client";
import {
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  ErrorState,
  InlineAlert,
  LoadingState,
  StatusBadge,
} from "../../shared/ui";
import type { ScopeChangeView, ScopeSummary } from "../../shared/types/api";
import { SCOPE_KIND_LABELS, SCOPE_KIND_TONE, formatPercent } from "./changeVocabulary";

/**
 * What has happened to this project's scope since it was agreed (§104).
 *
 * <p>Removed work is the half that matters and the half a system usually cannot show: once a
 * story is deleted there is nothing left to count. This reads a ledger written as the changes
 * happened, so a project that added two stories and quietly dropped three shows both.
 */
export function ProjectScopeTab({ projectId }: { projectId: string }) {
  const summary = useQuery({
    queryKey: ["scope-summary", projectId],
    queryFn: () => api.get<ScopeSummary>(`/api/v1/projects/${projectId}/scope-summary`),
  });

  const ledger = useQuery({
    queryKey: ["scope-changes", projectId],
    queryFn: () => api.get<ScopeChangeView[]>(`/api/v1/projects/${projectId}/scope-changes`),
  });

  if (summary.isLoading) {
    return <LoadingState label="Loading scope" />;
  }
  if (summary.isError) {
    return <ErrorState error={summary.error} onRetry={() => void summary.refetch()} />;
  }

  if (!summary.data?.hasBaseline) {
    return (
      <Card>
        <CardBody>
          <InlineAlert tone="info">
            Scope is measured against an agreed baseline, and this project does not have one yet.
            Until it does, everything created here is the plan rather than a departure from it.
          </InlineAlert>
        </CardBody>
      </Card>
    );
  }

  const scope = summary.data;

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        <Figure label="Originally committed" value={hours(scope.originalHours)} />
        <Figure label="Added" value={hours(scope.addedHours)} tone="amber" />
        <Figure label="Removed" value={hours(scope.removedHours)} tone="blue" />
        <Figure label="Re-estimated" value={hours(scope.reestimatedHours)} tone="violet" />
        <Figure
          label="Net change"
          value={hours(scope.netChangeHours)}
          note={
            scope.netChangeShare == null
              ? undefined
              : `${formatPercent(scope.netChangeShare)} of the commitment`
          }
          tone={(scope.netChangeHours ?? 0) > 0 ? "amber" : "green"}
          strong
        />
      </div>

      <Card>
        <CardHeader
          title="Every change since the commitment"
          subtitle={`Against baseline V${scope.baselineVersion ?? "?"} · ${scope.itemsAdded} added, ${scope.itemsRemoved} removed`}
        />
        <CardBody className="p-0">
          {ledger.isPending ? (
            <LoadingState label="Loading changes…" />
          ) : ledger.isError ? (
            <ErrorState error={ledger.error} onRetry={() => void ledger.refetch()} />
          ) : ledger.data.length === 0 ? (
            <EmptyState
              title="Nothing has changed"
              message="No work has been added, removed or re-estimated since the baseline was approved."
            />
          ) : (
            <ul className="divide-y divide-ink-100">
              {ledger.data.map((change) => (
                <li
                  key={change.id}
                  className="flex flex-wrap items-center justify-between gap-3 px-4 py-3"
                >
                  <div className="min-w-0">
                    <p className="text-sm text-ink-900">
                      {change.itemStillExists && change.workItemId ? (
                        <Link
                          to={`/work-items/${change.workItemKey}`}
                          className="font-mono text-xs text-ink-500 hover:text-brand-600"
                        >
                          {change.workItemKey}
                        </Link>
                      ) : (
                        <span className="font-mono text-xs text-ink-400">
                          {change.workItemKey}
                        </span>
                      )}{" "}
                      <span className={change.itemStillExists ? "" : "line-through decoration-ink-300"}>
                        {change.workItemTitle}
                      </span>
                    </p>
                    <p className="mt-0.5 text-xs text-ink-500">
                      {change.recordedByName ?? "System"} ·{" "}
                      {new Date(change.recordedAt).toLocaleDateString()}
                      {change.reason && ` · ${change.reason}`}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-3">
                    <StatusBadge tone={SCOPE_KIND_TONE[change.changeKind]}>
                      {SCOPE_KIND_LABELS[change.changeKind]}
                    </StatusBadge>
                    <span
                      className={
                        (change.hoursDelta ?? 0) > 0
                          ? "tnum w-20 text-right text-sm text-amber-700"
                          : "tnum w-20 text-right text-sm text-emerald-700"
                      }
                    >
                      {signedHours(change.hoursDelta)}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

function hours(value: number | null | undefined): string {
  if (value == null) {
    return "—";
  }
  const rounded = Math.round(value * 10) / 10;
  return `${rounded}h`;
}

function signedHours(value: number | null | undefined): string {
  if (value == null) {
    return "—";
  }
  const rounded = Math.round(value * 10) / 10;
  return rounded > 0 ? `+${rounded}h` : `${rounded}h`;
}

function Figure({
  label,
  value,
  note,
  tone,
  strong,
}: {
  label: string;
  value: string;
  note?: string;
  tone?: "amber" | "blue" | "violet" | "green";
  strong?: boolean;
}) {
  const colour =
    tone === "amber"
      ? "text-amber-700"
      : tone === "blue"
        ? "text-brand-700"
        : tone === "violet"
          ? "text-violet-700"
          : tone === "green"
            ? "text-emerald-700"
            : "text-ink-900";
  return (
    <div className="rounded-xl border border-ink-200 bg-surface px-4 py-3">
      <p className="text-xs text-ink-500">{label}</p>
      <p className={`tnum mt-1 ${strong ? "text-2xl" : "text-xl"} font-semibold ${colour}`}>
        {value}
      </p>
      {note && <p className="mt-0.5 text-xs text-ink-500">{note}</p>}
    </div>
  );
}
